/** Icelandic alphabetical order (A Á B D Ð E É … Þ Æ Ö) for song titles, regardless of UI language. */
let cached: Intl.Collator | null = null;

export function isCollator(): Intl.Collator {
  cached ??= new Intl.Collator('is', { numeric: true, sensitivity: 'variant' });
  return cached;
}

/** Leading quotes, brackets, dashes, dots … don't decide where a title sorts („Heyr …“ sorts under H). */
function sortKey(title: string): string {
  return title.trim().replace(/^[^\p{L}\p{N}]+/u, '');
}

/**
 * Icelandic order by the first letter/digit, matching indexLetter(), so groupByLetter() gets each
 * letter's titles in one run. Ties (only leading punctuation differs) fall back to the full title.
 */
export function compareTitles(a: string, b: string): number {
  const c = isCollator();
  return c.compare(sortKey(a), sortKey(b)) || c.compare(a.trim(), b.trim());
}

export function sortByTitle<T extends { title: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => compareTitles(a.title, b.title));
}

/** Index letter for the A–Ö list: first letter (Á, Ð, Þ, Æ, Ö are letters of their own); '#' otherwise. */
export function indexLetter(title: string): string {
  const m = /[\p{L}\p{N}]/u.exec(title);
  if (!m) return '#';
  const ch = m[0];
  return /\p{L}/u.test(ch) ? ch.toLocaleUpperCase('is') : '#';
}

/** Group an already-sorted list by index letter, preserving order of first appearance. */
export function groupByLetter<T extends { title: string }>(sorted: readonly T[]): { letter: string; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const it of sorted) {
    const l = indexLetter(it.title);
    const g = groups.get(l);
    if (g) g.push(it);
    else groups.set(l, [it]);
  }
  return [...groups.entries()].map(([letter, items]) => ({ letter, items }));
}

/** Collections in editor-defined order (sort), then alphabetically. */
export function sortCollections<T extends { sort: number; name: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.sort - b.sort || isCollator().compare(a.name, b.name));
}
